import React, { useContext, useEffect } from 'react'
import { useDispatch } from 'react-redux';
import { NoRowsLayout } from '../../../../components/NoRows/NoRowsLayout';
import Loading from '../../../../components/Loader/Loading';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Chip, Grid, IconButton, Switch, Typography } from '@mui/material';
import { Delete, Edit, RemoveRedEye } from '@mui/icons-material';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { formatDateTime } from '../../../../utils/dateUtils';
import { SectionContext } from '../Section/Sections';
import { useNavigate } from 'react-router-dom';
import useResponsive from '../../../../hooks/useResponsive';

const sectionTypeColors = {
    horizontal_scroll: { bg: '#e3f2fd', color: '#1565c0' },
    carousel: { bg: '#f3e5f5', color: '#7b1fa2' },
    grid_2_column: { bg: '#e8f5e9', color: '#2e7d32' },
    grid_3_column: { bg: '#fff3e0', color: '#e65100' },
    grid_4_column: { bg: '#fce4ec', color: '#c2185b' },
    fixed_banner: { bg: '#e0f7fa', color: '#00838f' },
    vertical_list: { bg: '#f1f8e9', color: '#558b2f' },
    mixed_grid: { bg: '#ede7f6', color: '#512da8' }
};

const backgroundTypeColors = {
    color: { bg: '#e3f2fd', color: '#1565c0' },
    image: { bg: '#f3e5f5', color: '#7b1fa2' },
    gradient: { bg: '#fff3e0', color: '#e65100' },
    none: { bg: '#f5f5f5', color: '#757575' }
};

const getSectionStatus = (row) => {
    const now = new Date();
    const start = row.startDateTime ? new Date(row.startDateTime) : null;
    const end = row.endDateTime ? new Date(row.endDateTime) : null;

    if (!row.isActive) return { label: 'Inactive', isLive: false, color: 'error.main', disabled: false };
    if (start && start > now) return { label: 'Scheduled', isLive: false, color: 'error.main', disabled: true };
    if (end && end < now) return { label: 'Inactive', isLive: false, color: 'error.main', disabled: false };
    return { label: 'Active', isLive: true, color: 'success.main', disabled: false };
};

const HeaderManagement = () => {
    const menuDispatch = useDispatch();
    useEffect(() => {
        const isFooterManagement = window.location.pathname.includes('/footer-management');
        menuDispatch({ type: 'menuSelect', payload: isFooterManagement ? 'footer-management' : 'header-management' });
    }, []);

    const { isSm } = useResponsive();
    const navigate = useNavigate();
    const {
        list,
        loading,
        allRowCount,
        paginationModel,
        handlePaginationChange,
        handleSortingChange,
        sortModel,
        handleStatusChange,
        handleDeleteSection
    } = useContext(SectionContext);

    const currentPageType = paginationModel.pageType || 'header';
    const isFooterManagement = currentPageType === 'footer';
    const getListNumber = (params) => {
        const visibleIndex = params.api?.getRowIndexRelativeToVisibleRows?.(params.id);
        const rowIndex = visibleIndex >= 0 ? visibleIndex : list.findIndex(row => row.id === params.id);
        if (rowIndex < 0) return '-';
        return (paginationModel.page * paginationModel.pageSize) + rowIndex + 1;
    };
    const columns = [
        {
            field: 'position',
            headerName: 'No.',
            headerAlign: 'center',
            flex: 0.5,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 28, height: 28,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.75rem', fontWeight: 'bold'
                }}>
                    {getListNumber(params)}
                </Box>
            )
        },
        {
            field: 'title',
            headerName: isFooterManagement ? 'Footer Name' : 'Header Name',
            headerAlign: 'center',
            flex: 1.5,
            sortable: true,
            renderCell: (params) => (
                <Box sx={{ width: '100%' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {params.row.title || 'N/A'}
                    </Typography>
                    {params.row.subtitle && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                            {params.row.subtitle}
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            field: 'sectionType',
            headerName: 'Type',
            headerAlign: 'center',
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const type = params.row.sectionType;
                const colors = sectionTypeColors[type] || { bg: '#f5f5f5', color: '#757575' };
                return (
                    <Chip
                        label={type?.replace(/_/g, ' ') || 'N/A'}
                        size="small"
                        sx={{
                            backgroundColor: colors.bg,
                            color: colors.color,
                            fontSize: '0.65rem',
                            textTransform: 'capitalize',
                            fontWeight: 500
                        }}
                    />
                );
            }
        },
        {
            field: 'backgroundType',
            headerName: 'Background',
            headerAlign: 'center',
            flex: 0.8,
            sortable: false,
            renderCell: (params) => {
                const type = params.row.backgroundType;
                const colors = backgroundTypeColors[type] || { bg: '#f5f5f5', color: '#757575' };
                return (
                    <Chip
                        label={type || 'none'}
                        size="small"
                        sx={{
                            backgroundColor: colors.bg,
                            color: colors.color,
                            fontSize: '0.65rem',
                            textTransform: 'capitalize',
                            fontWeight: 500
                        }}
                    />
                );
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            headerAlign: 'center',
            flex: 0.6,
            sortable: false,
            renderCell: (params) => {
                const status = getSectionStatus(params.row);

                return (
                    <PermissionGuard feature='customer-app' permission='canEdit'>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', width: '100%' }}>
                            <Switch
                                size='small'
                                checked={status.isLive}
                                disabled={status.disabled}
                                onChange={() => handleStatusChange({ isActive: !params.row.isActive }, params.row.id)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: 'success.main' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'success.main' },
                                    '& .MuiSwitch-switchBase': { color: 'error.main' },
                                    '& .MuiSwitch-track': { backgroundColor: 'error.main' }
                                }}
                                inputProps={{ 'aria-label': 'controlled' }}
                            />
                            <Typography sx={{
                                color: status.color,
                                fontWeight: '600', fontSize: '0.6rem'
                            }}>
                                {status.label}
                            </Typography>
                        </Box>
                    </PermissionGuard>
                );
            }
        },
        {
            field: 'activePeriod',
            headerName: 'Active Period',
            headerAlign: 'center',
            flex: 1.2,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.6rem' }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>Start: </Box>
                        {params.row.startDateTime ? formatDateTime(params.row.startDateTime) : 'N/A'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem' }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>End: </Box>
                        {params.row.endDateTime ? formatDateTime(params.row.endDateTime) : 'N/A'}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'createdAt',
            headerName: 'Created On',
            headerAlign: 'center',
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.65rem' }}>
                    {params.row.createdAt ? formatDateTime(params.row.createdAt) : ''}
                </Typography>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerAlign: 'center',
            flex: 0.8,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ p: 1, display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <IconButton
                        sx={{ color: 'blue' }}
                        onClick={() => navigate(`/customer-app/home-screen/section/view/${params.row.id}?pageType=${currentPageType}`)}
                        size="small"
                    >
                        <RemoveRedEye sx={{ fontSize: '0.95rem' }} />
                    </IconButton>
                    <PermissionGuard feature='customer-app' permission='canEdit'>
                        <IconButton
                            onClick={() => navigate(`/customer-app/home-screen/section/${params.row.id}?pageType=${currentPageType}`)}
                            size="small"
                            color='warning'
                        >
                            <Edit sx={{ fontSize: '0.95rem' }} />
                        </IconButton>
                    </PermissionGuard>
                    <PermissionGuard feature='customer-app' permission='canDelete'>
                        <IconButton
                            onClick={() => handleDeleteSection(params.row.id)}
                            size="small"
                            color='error'
                        >
                            <Delete sx={{ fontSize: '0.95rem' }} />
                        </IconButton>
                    </PermissionGuard>
                </Box>
            )
        }
    ];

    return (
        <Box sx={{ overflow: 'auto' }}>
            <Grid container spacing={2}>
                {/* DataGrid */}
                <Grid item xs={12}>
                    <Box sx={{ width: '100%', display: 'table', tableLayout: 'fixed', backgroundColor: '#fff', borderRadius: '.5rem' }}>
                        <DataGrid
                            rows={list}
                            columns={columns.map((col) => ({
                                ...col,
                                minWidth: isSm && (col.minWidth || 150)
                            }))}
                            getRowId={(row) => row.id}
                            rowCount={allRowCount}
                            pageSizeOptions={[5, 10, 20, 50, 100]}
                            paginationModel={paginationModel}
                            paginationMode="server"
                            onPaginationModelChange={handlePaginationChange}
                            sortingMode="server"
                            sortModel={sortModel}
                            onSortModelChange={handleSortingChange}
                            disableMultiSort
                            loading={loading}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            slots={{ loadingOverlay: Loading, noRowsOverlay: NoRowsLayout }}
                            autoHeight
                            getRowHeight={() => 'auto'}
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    border: 1, borderRight: 0, borderTop: 0,
                                    borderColor: 'lightgrey',
                                    display: 'flex', alignItems: 'center',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '2px solid rgba(224, 224, 224, 1)',
                                    borderTop: '2px solid rgba(224, 224, 224, 1)',
                                    fontWeight: 600, fontSize: '0.75rem',
                                    p: 0, height: 'fit-content',
                                    minHeight: '0 !important', height: '2rem'
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    borderLeft: '1px solid rgba(224, 224, 224, 1)',
                                    '&:last-child': { borderRight: 'none' },
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 600, color: '#3e3e3e'
                                },
                                '& .MuiDataGrid-row': {
                                    '&:last-child .MuiDataGrid-cell': {
                                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                    },
                                    '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                                },
                                '& .MuiDataGrid-columnSeparator': { display: 'none' },
                            }}
                        />
                    </Box>
                </Grid>

            </Grid>
        </Box>
    );
};

export default HeaderManagement;
